import { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  message,
  Divider,
  Typography,
  Row,
  Col,
  InputNumber,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  BookOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "../api/api";

const { Option } = Select;
const { Title, Text } = Typography;

export default function CrearUsuario() {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const navigate = useNavigate();

  /* ===================== LOAD EBOOKS ===================== */
  useEffect(() => {
    api
      .get("/api/GetEbooks")
      .then((res) => setEbooks(res.data))
      .catch(() => message.error("No se pudieron cargar los ebooks"));
  }, []);

  /* ===================== EBOOK CHANGE ===================== */
  const onEbookChange = (ebookId) => {
    const ebook = ebooks.find((e) => e.id === ebookId);
    if (ebook) {
      form.setFieldsValue({
        bid: ebook.idNormaProveedor,
      });
    }
  };

  /* ===================== SUBMIT ===================== */
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const days = Number(values.accessDays);

      const accessExpirationDate = dayjs()
        .add(days, "day")
        .format("YYYY-MM-DD");

      const payload = {
        email: values.email,
        name: values.name,
        bid: Number(values.bid),
        active: 1,
        accessExpirationDate,
      };

      await api.post("/api/AdduserEbook", payload);
      message.success("Usuario creado correctamente");
      form.resetFields();
    } catch (error) {
      message.error("Error al crear usuario");
    } finally {
      setLoading(false);
    }
  };

  /* ===================== UI ===================== */
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "linear-gradient(135deg,#e6f7ff,#f9f9f9)",
      }}
    >
      <Card
        style={{
          width: "100%",
          maxWidth: 1000,
          borderRadius: 24,
          boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
        }}
        bodyStyle={{ padding: 32 }}
      >
        {/* ================= HEADER ================= */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            Crear Usuario eBook
          </Title>
          <Text type="secondary">
            Gestión de accesos digitales a normas y publicaciones
          </Text>
        </div>

        <Divider />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          {/* ================= DATOS USUARIO ================= */}
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              background: "#fafafa",
              marginBottom: 24,
            }}
          >
            <Title level={5}>
              <UserOutlined /> Datos del usuario
            </Title>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="email"
                  label="Correo electrónico"
                  rules={[{ required: true, type: "email" }]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    size="large"
                    placeholder="usuario@correo.com"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Nombre completo"
                  rules={[{ required: true }]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    size="large"
                    placeholder="Nombre del usuario"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ================= EBOOK ================= */}
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              background: "#f6ffed",
              marginBottom: 24,
            }}
          >
            <Title level={5}>
              <BookOutlined /> Acceso a eBooks
            </Title>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="libId"
                  label="Seleccionar eBook"
                  rules={[{ required: true }]}
                >
                  <Select
                    size="large"
                    placeholder="Selccione un eBook"
                    onChange={onEbookChange}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {ebooks.map((e) => (
                      <Option key={e.id} value={e.id}>
                        {e.normaEbook}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="bid"
                  label="ID Proveedor"
                  rules={[{ required: true }]}
                >
                  <Input size="large" disabled />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ================= VIGENCIA ================= */}
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              background: "#fff7e6",
              marginBottom: 32,
            }}
          >
            <Title level={5}>
              <CalendarOutlined /> Vigencia de acceso
            </Title>

            <Form.Item
              name="accessDays"
              label="Duración del acceso (días)"
              rules={[{ required: true, message: "Ingrese los días" }]}
            >
              <InputNumber
                size="large"
                style={{ width: "100%" }}
                min={1}
                placeholder="Ej: 30, 90, 365..."
              />
            </Form.Item>
          </Card>

          {/* ================= BOTONES ================= */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
                style={{
                  height: 50,
                  borderRadius: 14,
                  fontSize: 16,
                  background: "linear-gradient(90deg,#1890ff,#40a9ff)",
                }}
              >
                Crear Usuario
              </Button>
            </Col>

            <Col xs={24} md={12}>
              <Button
                icon={<ArrowLeftOutlined />}
                size="large"
                block
                style={{ height: 50, borderRadius: 14 }}
                onClick={() => navigate("/dashboard/usuarios")}
              >
                Volver
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
}
